import { useEffect, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { times } from 'lodash';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from '@agro1desenvolvimento/react-components';
import { OperacoesService, UsagesService } from '../../services';
import type { UsuarioResumo } from '../../services/operacoes';
import type { Usage } from '../../services/usages';
import Agro1Toast from '../../components/Agro1Toast';
import useDisablePageScroll from '../../hooks/use-disable-page-scroll';

const SKELETON_ROWS_COUNT = 8;
const SKELETON_WIDTH_BASE = 60;
const SKELETON_WIDTH_STEP = 15;
const SKELETON_WIDTH_CYCLE = 3;

const UsageStatisticsSkeleton = () => (
  <div className="usage-statistics-dialog-content">
    <Skeleton height="2.2rem" />
    <table className="usage-statistics-dialog-table">
      <thead>
        <tr>
          <th>Descrição</th>
          <th>Quantidade</th>
        </tr>
      </thead>
      <tbody>
        {times(SKELETON_ROWS_COUNT, (i) => (
          <tr key={i}>
            <td>
              <Skeleton
                width={`${
                  SKELETON_WIDTH_BASE +
                  (i % SKELETON_WIDTH_CYCLE) * SKELETON_WIDTH_STEP
                }%`}
              />
            </td>
            <td>
              <Skeleton width="2rem" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

type Props = {
  visible: boolean;
  onHide: () => void;
};

const UsageStatisticsDialog = ({ visible, onHide }: Props) => {
  useDisablePageScroll(visible);

  const [usuarios, setUsuarios] = useState<UsuarioResumo[]>([]);
  const [usuario, setUsuario] = useState<UsuarioResumo | null>(null);
  const [usages, setUsages] = useState<Usage[]>([]);
  const [loading, setLoading] = useState(false);

  const loadUsages = useImmutableCallback(async (usuarioId?: number) => {
    setLoading(true);
    try {
      const data = await UsagesService.all({ usuario: usuarioId });
      setUsages(data);
    } catch {
      Agro1Toast.show({
        detail: 'Ocorreu um erro ao carregar as estatísticas de uso.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  });

  const load = useImmutableCallback(async () => {
    setLoading(true);
    try {
      const [usuariosData, usagesData] = await Promise.all([
        OperacoesService.usuariosAtivos(),
        UsagesService.all(),
      ]);
      setUsuarios(
        [...usuariosData].sort((a, b) =>
          a.nomeCompleto.localeCompare(b.nomeCompleto),
        ),
      );
      setUsages(usagesData);
    } catch {
      Agro1Toast.show({
        detail: 'Ocorreu um erro ao carregar as estatísticas de uso.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!visible) return;
    load();
  }, [visible, load]);

  const handleUsuarioChange = useImmutableCallback(
    (value: UsuarioResumo | null) => {
      setUsuario(value);
      loadUsages(value?.id);
    },
  );

  return (
    <Dialog
      header="Estatísticas de uso dos últimos 90 dias"
      visible={visible}
      onHide={onHide}
      className="usage-statistics-dialog"
      draggable={false}
    >
      {loading ? (
        <UsageStatisticsSkeleton />
      ) : (
        <div className="usage-statistics-dialog-content">
          <Dropdown
            value={usuario}
            options={usuarios}
            onChange={(e) => handleUsuarioChange(e.value)}
            optionLabel="nomeCompleto"
            placeholder="Usuário"
            filter
            showClear
            className="w-full"
            appendTo={document.body}
          />

          <div className="usage-statistics-table-wrapper">
            <table className="usage-statistics-dialog-table">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {usages.map(([descricao, quantidade]) => (
                  <tr key={descricao}>
                    <td>{descricao}</td>
                    <td>{quantidade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default UsageStatisticsDialog;
